import { useState, useEffect } from 'react';
import { formatDateMedium, formatDateTimeMedium } from '../../../../utils/formatters';
import type { SupabaseClient } from '@supabase/supabase-js';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import EmptyState from '../../../../components/EmptyState';
import { StatusBadge } from '../../../../components/DataTable';
import { POS_GOLD } from './constants';

interface CertContext {
  type: 'customer' | 'property' | 'room';
  id: string;
  name: string;
  propertyId?: string;
  propertyName?: string;
  properties?: { id: string }[];
}

interface CertificatesModalProps {
  context: CertContext;
  onClose: () => void;
  supabase: SupabaseClient;
}

interface CertSubmission {
  id: string;
  service_date: string | null;
  invoice_number: string | null;
  asset_ref: string | null;
  customer_name: string | null;
  operator_name: string | null;
  submission_status: string;
  authorized_at: string | null;
  primary_asset_description: string | null;
  property_id: string | null;
  room_id: string | null;
}

export default function CertificatesModal({ context, onClose, supabase }: CertificatesModalProps) {
  const [certificates, setCertificates] = useState<CertSubmission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCertificates();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional deps
  }, [context]);

  const loadCertificates = async (): Promise<void> => {
    try {
      setLoading(true);

      const cols = 'id, service_date, invoice_number, asset_ref, customer_name, operator_name, submission_status, authorized_at, primary_asset_description, property_id, room_id';

      if (context.type === 'customer') {
        const propertyIds = context.properties?.map(p => p.id) || [];
        if (propertyIds.length === 0) {
          setCertificates([]);
          setLoading(false);
          return;
        }
        const { data, error } = await supabase
          .from('cert_submissions').select(cols)
          .in('property_id', propertyIds)
          .order('service_date', { ascending: false })
          .limit(100);
        if (error) throw error;
        setCertificates(((data as CertSubmission[]) || []).filter(c =>
          ['certified', 'verified', 'authorized'].includes(c.submission_status),
        ));
        return;
      }

      if (context.type === 'room') {
        // POS batch certs usually sit at property level (room_id null)
        // with the room names embedded in primary_asset_description.
        // So we fetch BOTH: room-specific certs + property-wide certs
        // for the parent property, tagged so the UI can distinguish.
        const propertyId = context.propertyId;
        const [roomCerts, propertyCerts] = await Promise.all([
          supabase.from('cert_submissions').select(cols)
            .eq('room_id', context.id)
            .order('service_date', { ascending: false })
            .limit(50),
          propertyId
            ? supabase.from('cert_submissions').select(cols)
                .eq('property_id', propertyId)
                .is('room_id', null)
                .order('service_date', { ascending: false })
                .limit(50)
            : Promise.resolve({ data: [] as CertSubmission[], error: null as unknown }),
        ]);
        if (roomCerts.error) throw roomCerts.error;
        if (propertyCerts.error) throw propertyCerts.error;
        const combined = [
          ...((roomCerts.data as CertSubmission[]) || []),
          ...((propertyCerts.data as CertSubmission[]) || []),
        ].filter(c => ['certified', 'verified', 'authorized'].includes(c.submission_status));
        // De-dupe by id and sort newest first
        const byId = new Map(combined.map(c => [c.id, c]));
        const sorted = Array.from(byId.values()).sort((a, b) =>
          (b.service_date || '').localeCompare(a.service_date || ''),
        );
        setCertificates(sorted);
        return;
      }

      // property branch
      const { data, error } = await supabase
        .from('cert_submissions').select(cols)
        .eq('property_id', context.id)
        .order('service_date', { ascending: false })
        .limit(100);
      if (error) throw error;
      setCertificates(((data as CertSubmission[]) || []).filter(c =>
        ['certified', 'verified', 'authorized'].includes(c.submission_status),
      ));
    } catch (err: unknown) {
      console.error('Error loading certificates:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (cert: CertSubmission): { label: string; bg: string; color: string } => {
    const status = cert.submission_status;
    if (status === 'certified') return { label: 'Certified', bg: 'var(--color-success-bg)', color: 'var(--color-success)' };
    if (status === 'authorized') return { label: 'Authorized', bg: 'var(--color-success-bg)', color: 'var(--color-success)' };
    if (status === 'verified') return { label: 'Verified', bg: 'var(--color-info-bg)', color: 'var(--color-info-text)' };
    if (status === 'pending') return { label: 'Pending', bg: 'var(--color-warning-bg)', color: 'var(--color-warning)' };
    if (status === 'returned') return { label: 'Returned', bg: 'var(--color-error-bg)', color: 'var(--color-error)' };
    return { label: status || 'Unknown', bg: 'var(--color-border)', color: 'var(--color-text-light)' };
  };

  const getContextTitle = (): string => {
    if (context.type === 'customer') return `Certificates for ${context.name}`;
    if (context.type === 'property') return `Certificates for ${context.name}`;
    if (context.type === 'room') return `Certificates for ${context.name}`;
    return 'Certificates';
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    return formatDateMedium(dateStr);
  };

  const formatDateTime = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    return formatDateTimeMedium(dateStr);
  };

  const getCertificateUrl = (certId: string): string => {
    return `/certificate.html?id=${certId}`;
  };

  const getRoomDisplay = (cert: CertSubmission): string | null => {
    if (cert.primary_asset_description) return cert.primary_asset_description;
    return null;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '95%' }}>
        <div className="modal-header">
          <h2 className="modal-title">{getContextTitle()}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflow: 'auto', padding: 0 }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <LoadingSpinner />
            </div>
          ) : certificates.length === 0 ? (
            <EmptyState message="No certificates found." />
          ) : (
            <div className="flex flex-col">
              {certificates.map((cert, idx) => {
                const status = getStatusBadge(cert);
                const roomDisplay = getRoomDisplay(cert);
                return (
                  <div
                    key={cert.id}
                    style={{
                      padding: '1rem 1.25rem',
                      borderBottom: idx < certificates.length - 1 ? '1px solid var(--color-border)' : 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem'
                    }}
                  >
                    <div className="flex items-start justify-between flex-wrap gap-lg">
                      <div className="flex items-center flex-wrap gap-md">
                        <span style={{ fontWeight: 600, fontSize: 'var(--text-xl)', color: POS_GOLD }}>
                          {cert.invoice_number || cert.asset_ref || `CERT-${cert.id.slice(0, 8)}`}
                        </span>
                        <StatusBadge bg={status.bg} color={status.color}>{status.label}</StatusBadge>
                        {context.type === 'room' && !cert.room_id && (
                          <StatusBadge bg="var(--color-warning-bg)" color="var(--color-warning-text)">Property-wide</StatusBadge>
                        )}
                      </div>
                      <div className="flex gap-sm">
                        <a
                          href={getCertificateUrl(cert.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary"
                          style={{ fontSize: 'var(--text-sm)', padding: '0.375rem 0.75rem' }}
                        >
                          View Certificate
                        </a>
                      </div>
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                      gap: '0.75rem 1.5rem',
                      fontSize: 'var(--text-base)'
                    }}>
                      <div>
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.025em', marginBottom: '0.125rem' }}>Service Date</div>
                        <div className="fw-500">{formatDate(cert.service_date)}</div>
                      </div>
                      {roomDisplay && context.type !== 'room' && (
                        <div>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.025em', marginBottom: '0.125rem' }}>Room</div>
                          <div className="fw-500">{roomDisplay}</div>
                        </div>
                      )}
                      {cert.authorized_at && (
                        <div>
                          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.025em', marginBottom: '0.125rem' }}>Authorized</div>
                          <div className="fw-500">{formatDateTime(cert.authorized_at)}</div>
                        </div>
                      )}
                    </div>

                    {cert.primary_asset_description && cert.primary_asset_description !== roomDisplay && (
                      <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)', fontStyle: 'italic' }}>
                        {cert.primary_asset_description}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ borderTop: '1px solid var(--color-border)', padding: '0.75rem 1.25rem' }}>
          <div className="text-xs text-muted">
            Showing {certificates.length} certificate{certificates.length !== 1 ? 's' : ''}
          </div>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
